import { useQuery } from '@tanstack/react-query'

import { einvoiceKeys } from '@/hooks/use-einvoice-query'
import { einvoiceApi } from '@/lib/api/einvoice-api'

const FIVE_MINUTES = 5 * 60_000

/** Invoices made on MISA's website that no order owns, for the order's manual
 *  issue dialog. A suggestion is a convenience: any failure reads as "none"
 *  and the dialog stays a plain form. */
export function useManualCandidates(orderId: string) {
    const query = useQuery({
        queryKey: [...einvoiceKeys.all, 'manual-candidates', orderId],
        queryFn: async () => {
            const data = (await einvoiceApi.manualCandidates(orderId)).data
            return {
                items: data?.items ?? [],
                unavailable: Boolean(data?.warning),
            }
        },
        retry: false,
        staleTime: 30_000,
        // The list is MISA's live state; keep it at most five minutes.
        gcTime: FIVE_MINUTES,
    })
    return {
        candidates: query.data?.items ?? [],
        /** "unavailable" = MISA could not be asked; "ok" = it answered, even
         *  with nothing. Unknown until the request settles. */
        status: query.isError
            ? ('unavailable' as const)
            : query.isSuccess
              ? query.data.unavailable
                  ? ('unavailable' as const)
                  : ('ok' as const)
              : undefined,
        count: query.isSuccess
            ? query.data.items.length
            : query.isError
              ? 0
              : undefined,
    }
}
